import { useState, useEffect, useCallback } from 'react';
import { bookmarksService } from '../services/bookmarks.service';
import type { BookmarkFolder, CreateFolderRequest, UpdateFolderRequest } from '@/types/api.types';
import { useAuthStore } from '../store/auth.store';
import { asApiError } from '@/utils/error.util';

/**
 * Hook for managing bookmark folders
 */
export const useBookmarkFolders = () => {
  const [folders, setFolders] = useState<BookmarkFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuthStore();

  /**
   * Load user's bookmark folders
   */
  const loadFolders = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const data = await bookmarksService.getUserFolders();
      setFolders(data);
    } catch (err) {
      console.error('Failed to load folders:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Create a new folder
   */
  const createFolder = async (input: CreateFolderRequest) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      const folder = await bookmarksService.createFolder(input);

      // Add to local state
      setFolders(prev => [...prev, folder].sort((a, b) => a.position - b.position));

      return folder;
    } catch (err) {
      console.error('Failed to create folder:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to create folder');
      throw err;
    }
  };

  /**
   * Update a folder
   */
  const updateFolder = async (folderId: string, input: UpdateFolderRequest) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      const updated = await bookmarksService.updateFolder(folderId, input);

      // Update local state
      setFolders(prev =>
        prev.map(f => f.id === folderId ? updated : f)
           .sort((a, b) => a.position - b.position)
      );

      return updated;
    } catch (err) {
      console.error('Failed to update folder:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to update folder');
      throw err;
    }
  };

  /**
   * Delete a folder (bookmarks move to no folder)
   */
  const deleteFolder = async (folderId: string) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      await bookmarksService.deleteFolder(folderId);

      // Remove from local state
      setFolders(prev => prev.filter(f => f.id !== folderId));
    } catch (err) {
      console.error('Failed to delete folder:', err);
      setError(asApiError(err).response?.data?.message || 'Failed to delete folder');
      throw err;
    }
  };

  /**
   * Get folder by ID
   */
  const getFolder = (folderId: string): BookmarkFolder | undefined => {
    return folders.find(f => f.id === folderId);
  };

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  return {
    folders,
    loading,
    error,
    createFolder,
    updateFolder,
    deleteFolder,
    getFolder,
    refresh: loadFolders,
  };
};
